import {
  CabAutocomplete, CabButton, CabControlLabel, CabDropdown, CabIcon, CabSwitch, CabToggleChip, CabTooltip
} from "@CabComponents";
import CabMeetingLocationDropdown from "@CabComponents/CabMeetingLocationDropdown";
import {
  Box, FormControl, FormHelperText, FormLabel, Grid, IconButton, Typography, styled,
} from "@mui/material";
import { uniqBy } from "lodash-es";
import { useCallback, useMemo, useState } from "react";
import { Control, Controller, SubmitHandler, UseFormReturn, useFieldArray } from "react-hook-form";
import {
  Calendar, ConferenceContact, LeaderList, Meeting, MeetingRoom, MeetingUpdate, PresetLocations, User, ZoomSettings,
} from "../../../store";
import { getTemplateVars } from "../../../utils/scheduleUtils";
import type { LocationsFormInput } from "./types";
import { transformLocations } from "./meetingSettingsPayloads";
import { IoAddOutline, IoCloseOutline, IoSettingsOutline } from "react-icons/io5";
import colors from "../../../colors";
import InfoToolTip from "../../Common/InfoToolTip";
import AnalyticsEventColorPicker from "../../Pickers/AnalyticsEventColorPicker";
import { AnalyticsEventColor } from "../../../store/cabinetApi/generated/analytics";
import { PROVIDER } from "../../../constants";
import { checkForMicrosoftGrant } from "../../../utils/authUtils";
import { CabModal } from "@CabComponents/CabModal";
import { scheduling } from "../../../store/cabinetApi/scheduling";
import { useDebouncedCallback } from "use-debounce";

interface Props {
  isOwner: boolean;
  meeting: Partial<Meeting> & { id: number; };
  zoomSettings: {
    [settingsId: string]: ZoomSettings;
  };
  user: User | null | undefined;
  leaders: LeaderList;
  leaderCalendarOptions: Calendar[];
  onAddZoom: () => void;
  calendars: Calendar[];
  meetingRooms: { [id: string]: MeetingRoom }
  presetLocations: PresetLocations
  onOpenQuestionModal: () => void;
  control: Control;
  locationsForm: UseFormReturn<LocationsFormInput, undefined>;
  onSubmit: (meeting: MeetingUpdate, files?: File[]) => Promise<void>;
  noSelectedLeaders?: boolean;
  locations?: LocationsFormInput["locations"];
}

const CalendarAndLocation = ({
  isOwner, meeting, calendars, leaderCalendarOptions, zoomSettings, user, leaders, meetingRooms, 
  presetLocations, onAddZoom, onOpenQuestionModal, control, locations, locationsForm, onSubmit, noSelectedLeaders,
}: Props) => {
  const [defaultHoldColorPickerOpen, setDefaultHoldColorPickerOpen] = useState(false);
  const [zoomModalLocationIndex, setZoomModalLocationIndex] = useState<number | null>(null);
  const [zoomModalAltHosts, setZoomModalAltHosts] = useState<ConferenceContact[]>([]);
  const [zoomModalAutoRecording, setZoomModalAutoRecording] = useState<"none" | "local" | "cloud">("none");
  const [zoomModalJoinBeforeHost, setZoomModalJoinBeforeHost] = useState(false);
  const [zoomModalContacts, setZoomModalContacts] = useState<ConferenceContact[]>([]);
  const [loadingZoomModalContacts, setLoadingZoomModalContacts] = useState(false);
  const [zoomNoOptionsText, setZoomNoOptionsText] = useState("Start typing to find alternative hosts");
  const [getZoomContacts] = scheduling.useLazyAppSchedulingZoomUserContactsRetrieveQuery();
  
  const templateVars = useMemo(() => {
    return getTemplateVars(meeting.questions);
  }, [meeting.questions]);

  const {
    fields: locationFields, append, remove,
  } = useFieldArray({ control: locationsForm.control, name: 'locations' });

  let calendarOptions = leaderCalendarOptions.map(cal => ({
    value: cal.id, label: cal.summary
  }));

  // in case user does not have access to this calendar we must also include the current meeting calendar as an option,
  // then dedupe
  if (meeting.calendar_info?.calendar_name) {
    calendarOptions = uniqBy([
      ...calendarOptions,
      { value: meeting.calendar_info.id, label: meeting.calendar_info.calendar_name },
    ], cal => cal.value);
  }
  
  const calendar = useMemo(() => (
    calendars.find(cal => meeting.booking_calendar === cal.id)
  ), [meeting.booking_calendar, calendars]);

  const handleLocationsSubmit: SubmitHandler<LocationsFormInput> = useCallback(async (data) => {
    const payload = {
      id: meeting.id,
      ...transformLocations(data.locations),
    };

    await onSubmit(payload as MeetingUpdate, undefined);

  }, [meeting.id, onSubmit]);

  const getColorTooltip = useCallback((color?: AnalyticsEventColor) => (
    calendar?.provider === PROVIDER["MICROSOFT"].id 
      ? color?.category || "No Category Selected" 
      : color?.color ? "" : "Default Calendar Color"
  ), [calendar?.provider]);

  const showTokenInputs = !meeting.is_poll;
  const modalLoc = zoomModalLocationIndex == null ? undefined : locations?.[zoomModalLocationIndex];
  const modalLeaderSetting = modalLoc?.conference?.leader == null
    ? undefined
    : Object.values(zoomSettings).find(stg => stg.leader === modalLoc.conference?.leader);
  const zoomHostOptions = uniqBy(
    [...(modalLeaderSetting?.alternate_hosts || []), ...(zoomModalContacts || []), ...(zoomModalAltHosts || [])],
    c => c.account_user_id
  ).map(c => ({
    value: c.account_user_id,
    label: `${c.first_name || ""} ${c.last_name || ""} (${c.username})`.trim()
  }));
  const zoomHostLookup = uniqBy(
    [...(modalLeaderSetting?.alternate_hosts || []), ...(zoomModalContacts || []), ...(zoomModalAltHosts || [])],
    c => c.account_user_id
  ).reduce((acc, contact) => ({ ...acc, [contact.account_user_id]: contact }), {} as Record<string, ConferenceContact>);
  const hasZoomGrant = !!user?.oauth_grant_details?.[PROVIDER.ZOOM.name]
    && Object.keys(user.oauth_grant_details[PROVIDER.ZOOM.name] || {}).length > 0;
  const debouncedGetZoomContacts = useDebouncedCallback(async (searchStr: string) => {
    if (!searchStr || !hasZoomGrant) {
      setLoadingZoomModalContacts(false);
      return;
    }
    try {
      const res = await getZoomContacts(searchStr).unwrap();
      setZoomModalContacts(
        res.zoom_user_contacts.map(c => ({
          account_user_id: c.id,
          username: c.email,
          first_name: c.first_name,
          last_name: c.last_name,
        }))
      );
    } finally {
      setLoadingZoomModalContacts(false);
    }
  }, 400);

  if (!user) return null;

  return (
    <>
      <Grid container>
        <Grid size={12}>
          <Controller name="selectedCalendar" control={control} render={({ field: { ref, ...field } }) => (
            <FormControl sx={{ width: "100%" }}>
              <Box
                sx={{
                  display: 'flex',
                  gap: .5,
                  alignItems: 'center'
                }}>
                <StyledFormLabel>Calendar</StyledFormLabel>
                <InfoToolTip message="Meeting invitations will be sent from this calendar" fontSize={16} />
              </Box>
              <CabTooltip
                title={!isOwner ? 'Only meeting owner may edit this field' 
                  : noSelectedLeaders ? 'A leader must be attached to edit this field' : ''}
                placement="top"
                wrapWithSpan
              >
                <CabDropdown<number>
                  {...field}
                  value={noSelectedLeaders ? undefined : field.value}
                  options={calendarOptions}
                  disabled={!isOwner || noSelectedLeaders}
                  size="small"
                  sx={{ width: '100%' }}
                />
              </CabTooltip>
            </FormControl>
          )} />
        </Grid>

        <Grid size={12}
          sx={{ marginTop: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Controller name="selectedColor" control={control} render={({ field: { ref, ...field } }) => (
            <FormControl sx={{ width: "auto", minWidth: 150 }}>
              <StyledFormLabel>
                {calendar?.provider === PROVIDER["MICROSOFT"].id ? "Category" : "Color"}
              </StyledFormLabel>
              <CabTooltip
                title={!isOwner 
                  ? 'Only meeting owner may edit this field' 
                  : noSelectedLeaders 
                    ? 'A leader must be attached to edit this field' 
                    : getColorTooltip(field.value)}
                placement="top"
                wrapWithSpan
                enterDelay={500}
                leaveDelay={250}
              >
                <Box>
                  <CabToggleChip
                    disabled={field.disabled}
                    chipColor={field.value?.color || colors.white900}
                    label={
                      field.value?.color ? "" 
                        : field.value?.category ? field.value.category
                          : calendar?.provider === PROVIDER["MICROSOFT"].id 
                            ? "None" : "Default"
                    }
                    selected={true}
                    translateColor
                    sx={{
                      border: `1px solid ${field.value?.color || colors.black200}`,
                      width: "100%",
                      minWidth: 80,
                      cursor: "pointer",
                      borderRadius: "4px",
                      fontSize: "13px",
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%'
                      }
                    }}
                    onClick={() => setDefaultHoldColorPickerOpen(true)}
                  />
                </Box>
                <AnalyticsEventColorPicker
                  show={defaultHoldColorPickerOpen}
                  onSelect={color => {
                    field.onChange(color);
                    setDefaultHoldColorPickerOpen(false);
                  }}
                  onCancel={() => setDefaultHoldColorPickerOpen(false)}
                  calendar={calendar}
                />
              </CabTooltip>
            </FormControl>
          )} />
          <Controller
            name="privateVisibility"
            control={control}
            render={({ field: { value, onChange, ...field } }) => (
              <CabTooltip
                title={
                  !isOwner ? 'Only meeting owner may edit this field'
                    : noSelectedLeaders ? 'A leader must be attached to edit this field' : ''
                }
                placement="top"
                wrapWithSpan
              >
                <FormControl sx={{ width: "auto", paddingRight: 1 }}>
                  <StyledFormLabel>Private Event Visibility</StyledFormLabel>
                  <CabSwitch
                    {...field}
                    checked={!!value}
                    onChange={(e, checked) => onChange(e, checked)}
                    disabled={!isOwner || noSelectedLeaders}
                  />
                </FormControl>
              </CabTooltip>
            )}
          />
        </Grid>

        <Grid
          size={12}
          container
          sx={{
            marginTop: 2,
            marginBottom: '2px'
          }}>
          <StyledFormLabel>Location(s) and Conference Tool</StyledFormLabel>
        </Grid>
        <Grid size={12} container spacing={2}>
          {locationFields.map((locField, idx) => (
            <Controller
              key={locField.id}
              name={`locations.${idx}`}
              control={locationsForm.control}
              render={({ field: { ref, ...field } }) => (
                <>
                  <Grid size={isOwner && locations && locations.length > 1 ? 11.3 : 12}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        width: "100%"
                      }}>
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0
                        }}>
                        <CabTooltip
                          title={noSelectedLeaders ? 'A leader must be attached to edit this field' : ''}
                          placement="top"
                          wrapWithSpan
                        >
                          <CabMeetingLocationDropdown
                            secondaryInputOnNewLine
                            {...field}
                            value={noSelectedLeaders ? {} : {
                              // Omit settings: option values are only provider/leader; extra keys break JSON match.
                              conference: field.value?.conference
                                ? {
                                  provider: field.value.conference.provider,
                                  leader: field.value.conference.leader,
                                }
                                : undefined,
                              locationText: field.value?.locationText,
                              room: field.value?.room,
                              presetLocation: field.value?.presetLocation,
                              none: field.value?.none,
                            }}
                            onChange={loc => {
                              const next = loc?.conference && loc.conference.provider !== PROVIDER.ZOOM.id
                                ? {
                                  ...loc,
                                  conference: { ...loc.conference, settings: undefined },
                                }
                                : loc;
                              field.onChange(next);
                              if (!('locationText' in loc) && loc.room !== -1) {
                                locationsForm.handleSubmit(handleLocationsSubmit)();
                              }
                            }}
                            onBlurOtherField={() => locationsForm.handleSubmit(handleLocationsSubmit)()}
                            disabled={noSelectedLeaders}
                            showTokenInputs={showTokenInputs}
                            meeting={{
                              ...meeting,
                              // Ensure meeting has conference_provider/leader so dropdown validation works
                              conference_provider: field.value?.conference?.provider ?? meeting.conference_provider,
                              conference_leader: field.value?.conference?.leader ?? meeting.conference_leader,
                            }}
                            meetingBookingCalendar={calendar}
                            user={user}
                            leaders={leaders}
                            zoomSettings={zoomSettings}
                            isOwner={isOwner}
                            presetLocations={{...presetLocations, ...(meeting.location_presets_data || {})}}
                            meetingRooms={{...meetingRooms, ...(meeting.rooms_data || {})}}
                            onAddZoom={onAddZoom}
                            onAddQuestion={meeting.is_reusable || meeting.is_poll ?
                              () => onOpenQuestionModal()
                              : undefined}
                            templateVars={templateVars}
                            // only allow one virtual conference location
                            disableConferenceOptionsTooltip={locations?.some(loc => loc?.conference)
                              ? 'Only one conference integration can be set per meeting' : undefined}
                            disabledLocationPresetIds={locations?.filter(loc => loc?.presetLocation)
                              .map(loc => loc?.presetLocation) as number[]}
                            disabledMeetingRoomIds={
                              locations?.filter(loc => loc?.room).map(loc => loc?.room) as number[]
                            }
                            hasMicrosoftGrant={user ? checkForMicrosoftGrant(user.oauth_grant_details) : undefined}
                          />
                        </CabTooltip>
                      </Box>

                      {field.value?.conference?.provider === PROVIDER.ZOOM.id && (
                        <CabTooltip title="Zoom video settings" wrapWithSpan>
                          <span>
                            <IconButton
                              size="small"
                              disabled={!isOwner || !!noSelectedLeaders}
                              onClick={() => {
                                const rowLoc = field.value;
                                const leaderId = rowLoc?.conference?.leader;
                                const globalZoomForLeader = leaderId == null
                                  ? undefined
                                  : Object.values(zoomSettings).find(stg => stg.leader === leaderId);
                                const globalAltHosts = globalZoomForLeader?.alternate_hosts ?? [];
                                const persistedZoom = meeting.zoom_conference_settings;
                                const zoomForm = rowLoc?.conference?.settings;

                                setZoomModalAltHosts(
                                  zoomForm?.provider === 3
                                    ? zoomForm.alternativeHosts || []
                                    : persistedZoom == null
                                      ? globalAltHosts
                                      : persistedZoom.alternate_hosts ?? globalAltHosts
                                );
                                setZoomModalAutoRecording(
                                  zoomForm?.provider === 3
                                    ? zoomForm.autoRecording || "none"
                                    : persistedZoom?.auto_recording ?? "none"
                                );
                                setZoomModalJoinBeforeHost(
                                  zoomForm?.provider === 3
                                    ? !!zoomForm.joinBeforeHost
                                    : !!persistedZoom?.join_before_host
                                );
                                setZoomModalContacts([]);
                                setZoomNoOptionsText("Start typing to find alternative hosts");
                                setZoomModalLocationIndex(idx);
                              }}
                              sx={{
                                border: `1px solid ${colors.black200}`,
                                borderRadius: "4px",
                                marginTop: "2px",
                                color: colors.black700,
                              }}
                            >
                              <CabIcon Icon={IoSettingsOutline} sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </CabTooltip>
                      )}
                    </Box>
                  </Grid>

                  {isOwner && locations && locations.length > 1 && (
                    <Grid size={0.7} sx={{
                      marginLeft: -2.2
                    }}>
                      <CabIcon
                        Icon={IoCloseOutline}
                        onClick={() => {
                          remove(idx);
                          locationsForm.handleSubmit(handleLocationsSubmit)();
                        }}
                        sx={{ fontSize: 26, paddingTop: 0.8, }}
                      />
                    </Grid>
                  )}
                </>
              )} />
          ))}

          <Grid size={12}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                width: "100%"
              }}>
              <CabButton
                buttonType="tertiary"
                // disabled={saving}
                onClick={() => append({ none: true })}
                icon={<CabIcon Icon={IoAddOutline} />}
                sx={{ border: 1, borderColor: 'transparent' }}
              >
                Add Meeting Location
              </CabButton>
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <CabModal
        open={zoomModalLocationIndex != null}
        onClose={() => setZoomModalLocationIndex(null)}
        title="Zoom Video Settings"
        actionButtons={(
          <>
            <CabButton buttonType="secondary" onClick={() => setZoomModalLocationIndex(null)}>
              Cancel
            </CabButton>
            <CabButton
              onClick={() => {
                if (zoomModalLocationIndex == null) return;
                const existing = locationsForm.getValues(`locations.${zoomModalLocationIndex}`);
                locationsForm.setValue(
                  `locations.${zoomModalLocationIndex}`,
                  {
                    ...existing,
                    conference: existing?.conference ? {
                      ...existing.conference,
                      settings: {
                        provider: 3,
                        alternativeHosts: zoomModalAltHosts,
                        autoRecording: zoomModalAutoRecording,
                        joinBeforeHost: zoomModalJoinBeforeHost,
                      },
                    } : existing?.conference,
                  },
                  { shouldDirty: true }
                );
                setZoomModalLocationIndex(null);
                locationsForm.handleSubmit(handleLocationsSubmit)();
              }}
            >
              Save
            </CabButton>
          </>
        )}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            marginTop: 1
          }}>
          <Box>
            <Typography variant="subtitle2" sx={{
              marginBottom: 0.5
            }}>Alternative Hosts</Typography>
            <CabAutocomplete<string, never>
              loading={loadingZoomModalContacts}
              value={zoomModalAltHosts.map(host => host.account_user_id)}
              noOptionsText={zoomNoOptionsText}
              onChange={(val) => {
                if (!Array.isArray(val)) return;
                setZoomModalAltHosts(
                  val
                    .map(v => zoomHostLookup[String(v)] || null)
                    .filter((v): v is ConferenceContact => !!v)
                );
              }}
              onInputChange={(v) => {
                if (v.length === 0) {
                  setZoomNoOptionsText("Start typing to find alternative hosts");
                  setZoomModalContacts([]);
                  return;
                }
                setZoomNoOptionsText("There are no matching Zoom users at your organization");
                setLoadingZoomModalContacts(true);
                debouncedGetZoomContacts(v);
              }}
              options={zoomHostOptions}
              multiple
              hideArrow
              placeholder="Alternative Hosts"
            />
            <FormHelperText>Name or email (must be licensed Zoom user)</FormHelperText>
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{
              marginBottom: 0.5
            }}>Auto Recording</Typography>
            <CabDropdown<"none" | "local" | "cloud">
              size="small"
              value={zoomModalAutoRecording}
              onChange={e => setZoomModalAutoRecording(e.target.value as "none" | "local" | "cloud")}
              options={[
                { value: "none", label: "None" },
                { value: "local", label: "Local" },
                { value: "cloud", label: "Cloud" },
              ]}
              sx={{ width: "100%" }}
            />
          </Box>
          <Box>
            <CabControlLabel
              label="Allow participants to join before host"
              control={(
                <CabSwitch
                  checked={zoomModalJoinBeforeHost}
                  onChange={(_, checked) => setZoomModalJoinBeforeHost(checked)}
                />
              )}
            />
          </Box>
        </Box>
      </CabModal>
    </>
  );
};

export default CalendarAndLocation;

const StyledFormLabel = styled(FormLabel, { label: 'StyledFormLabel' })(() => ({
  fontSize: 12, fontWeight: 600, color: `${colors.calmGrey500} !important`
}));
